import React, { useEffect, useMemo, useState } from 'react';

import type { CurrencyCode } from 'apis/handler/exchange-rate';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Check, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  CURRENCY_META,
  FREQUENTLY_USED_CURRENCY_CODES,
  LANGUAGE_DEFAULT_CURRENCY,
  OTHER_CURRENCY_CODES,
  cn,
  useAvailableCurrencyCodes,
} from 'shared/lib';
import { useCurrencyStore } from 'store/useCurrencyStore';
import { useExchangeRateStore } from 'store/useExchangeRateStore';

import { LanguageSwitcherIcon } from './Icons';

type LanguageSwitcherProps = {
  // 'default' = 헤더용 흰 배경 hover, 'floating' = 모바일 매물 상세 사진 위 다크 반투명
  variant?: 'default' | 'floating';
};

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'vi', name: 'Tiếng Việt' },
  { code: 'zh', name: '中文' },
  { code: 'ja', name: '日本語' },
] as const;

/**
 * 언어 + 통화 선택 모달.
 * 글로브 아이콘을 트리거로 사용하고, 모달 안에서 두 가지를 함께 선택해 Apply.
 *
 * 언어 변경은 기존 path 기반 라우팅 + window.location.replace 동작 유지
 * (Google Maps 런타임 언어 변경 미지원 → 새로고침 필요).
 */
const LanguageSwitcher = ({ variant = 'default' }: LanguageSwitcherProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const selectedCurrency = useCurrencyStore((s) => s.selectedCurrency);
  const setSelectedCurrency = useCurrencyStore((s) => s.setSelectedCurrency);
  const updatedAt = useExchangeRateStore((s) => s.updatedAt);
  const availableCurrencies = useAvailableCurrencyCodes();

  const [open, setOpen] = useState(false);
  const [draftLanguage, setDraftLanguage] = useState(i18n.language);
  const [draftCurrency, setDraftCurrency] = useState<CurrencyCode>(selectedCurrency);

  // 모달 열릴 때마다 현재 값으로 draft 초기화
  useEffect(() => {
    if (open) {
      setDraftLanguage(i18n.language);
      setDraftCurrency(selectedCurrency);
    }
  }, [open, i18n.language, selectedCurrency]);

  const supportedLanguageCodes = useMemo(() => SUPPORTED_LANGUAGES.map((l) => l.code), []);

  const getNewPath = (newLanguage: string): string | null => {
    const pathSegments = location.pathname.split('/');
    const currentPathLanguage = pathSegments[1];
    const isCurrentPathLanguage = supportedLanguageCodes.includes(
      currentPathLanguage as (typeof supportedLanguageCodes)[number],
    );
    const remainingPath = pathSegments.slice(2).join('/');
    const queryString = location.search;

    if (newLanguage === i18n.language) return null;

    if (newLanguage === 'en') {
      return isCurrentPathLanguage ? `/${remainingPath}${queryString}` : null;
    }

    const newPath = isCurrentPathLanguage ? `/${newLanguage}/${remainingPath}` : `/${newLanguage}${location.pathname}`;
    return `${newPath}${queryString}`;
  };

  const handleApply = () => {
    const currencyChanged = draftCurrency !== selectedCurrency;
    const languageChanged = draftLanguage !== i18n.language;

    if (currencyChanged) {
      setSelectedCurrency(draftCurrency);
      window.dataLayer?.push({
        event: 'click_menu',
        menu: draftCurrency,
        section: 'Currency',
      });
    }

    const newPath = languageChanged ? getNewPath(draftLanguage) : null;

    setOpen(false);

    if (newPath) {
      const selectedLanguage = SUPPORTED_LANGUAGES.find((lang) => lang.code === draftLanguage);
      window.dataLayer?.push({
        event: 'click_menu',
        menu: selectedLanguage?.name ?? draftLanguage,
        section: 'Language',
      });

      // i18n도 변경 (페이지 재로드 전 짧은 순간 동기화)
      i18n.changeLanguage(draftLanguage);
      // 선택 언어를 감지기가 읽는 localStorage에 직접 기록 (i18n.js에서 caches를 꺼서
      // 여기가 유일한 저장 지점). changeLanguage의 비동기 캐시에 맡기면 아래
      // replace(새로고침)와 경합해 유실된다.
      try {
        window.localStorage.setItem('i18nextLng', draftLanguage);
      } catch {
        // localStorage 차단 환경(사생활 보호 모드 등)에서는 저장 없이 이동만 한다
      }
      // Google Maps 런타임 언어 변경 미지원 → replace로 새로고침
      window.location.replace(newPath);
    }
  };

  const isCurrencyAvailable = availableCurrencies.size > 1; // KRW만 있으면 환율 정보 없음

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {variant === 'floating' ? (
          <button
            className="flex h-9 items-center gap-1 rounded-full bg-black/40 pl-2 pr-2.5 text-white backdrop-blur-sm"
            aria-label="Language and Currency"
          >
            <Globe size={18} />
            <span className="text-xs font-medium leading-none">{selectedCurrency}</span>
          </button>
        ) : (
          <button
            className="flex h-11 items-center gap-1 rounded-full px-2.5 hover:bg-[#F6F6F6]"
            aria-label="Language and Currency"
          >
            <LanguageSwitcherIcon />
            <span className="text-gray-90 text-xs font-medium leading-none">{selectedCurrency}</span>
          </button>
        )}
      </DialogTrigger>

      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden rounded-[20px] border border-gray-200 bg-white p-0 sm:max-w-[640px]">
        <div className="flex items-center justify-between border-b border-[#EAEAEA] px-6 py-5">
          <DialogTitle className="text-base font-semibold text-[#222222]">{t('languageAndCurrency.title')}</DialogTitle>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Language section */}
          <section>
            <div className="mb-3 flex items-end justify-between">
              <h3 className="text-sm font-semibold text-[#222222]">{t('languageAndCurrency.language')}</h3>
              <span className="text-xs text-gray-40">{t('languageAndCurrency.languageHelp')}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {SUPPORTED_LANGUAGES.map((lang) => {
                const isActive = draftLanguage === lang.code;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => {
                      setDraftLanguage(lang.code);
                      // 언어 변경 시 통화 draft도 그 언어 default로 자동 매칭
                      // (사용자가 통화 grid에서 별도 선택하면 override 가능)
                      const nextCurrency = LANGUAGE_DEFAULT_CURRENCY[lang.code];
                      if (nextCurrency) setDraftCurrency(nextCurrency);
                    }}
                    className={cn(
                      'flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors',
                      isActive
                        ? 'border-black bg-black text-white'
                        : 'border-gray-200 bg-white text-[#222222] hover:bg-[#F6F6F6]',
                    )}
                  >
                    {isActive && <Check className="h-3.5 w-3.5" />}
                    {lang.name}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Currency section */}
          <section className="mt-8">
            <div className="mb-3 flex items-end justify-between">
              <h3 className="text-sm font-semibold text-[#222222]">{t('languageAndCurrency.currency')}</h3>
              <span className="text-xs text-gray-40">{t('languageAndCurrency.currencyHelp')}</span>
            </div>

            <div className="mb-4 flex items-start gap-2 rounded-lg bg-[#F6F6F6] px-4 py-3 text-xs text-gray-50">
              <span aria-hidden className="mt-0.5">
                ⓘ
              </span>
              <p className="leading-relaxed">{t('languageAndCurrency.paymentNotice')}</p>
            </div>

            {!isCurrencyAvailable ? (
              <div className="rounded-lg border border-gray-200 px-4 py-6 text-center text-sm text-gray-40">
                {t('languageAndCurrency.unavailable')}
              </div>
            ) : (
              <>
                <h4 className="mb-2 text-xs font-medium text-gray-50">{t('languageAndCurrency.frequentlyUsed')}</h4>
                <CurrencyGrid
                  codes={FREQUENTLY_USED_CURRENCY_CODES}
                  selected={draftCurrency}
                  available={availableCurrencies}
                  onSelect={setDraftCurrency}
                />

                <h4 className="mb-2 mt-5 text-xs font-medium text-gray-50">
                  {t('languageAndCurrency.otherCurrencies')}
                </h4>
                <CurrencyGrid
                  codes={OTHER_CURRENCY_CODES}
                  selected={draftCurrency}
                  available={availableCurrencies}
                  onSelect={setDraftCurrency}
                />
              </>
            )}
          </section>
        </div>

        <div className="flex items-center justify-between border-t border-[#EAEAEA] px-6 py-4">
          <span className="text-xs text-gray-40">
            {updatedAt
              ? t('languageAndCurrency.lastUpdated', { date: updatedAt })
              : t('languageAndCurrency.dailyUpdate')}
          </span>
          <button
            type="button"
            onClick={handleApply}
            className="rounded-full bg-black px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#222222]"
          >
            {t('languageAndCurrency.apply')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type CurrencyGridProps = {
  codes: readonly CurrencyCode[];
  selected: CurrencyCode;
  available: Set<CurrencyCode>;
  onSelect: (code: CurrencyCode) => void;
};

const CurrencyGrid = ({ codes, selected, available, onSelect }: CurrencyGridProps) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {codes.map((code) => {
        const meta = CURRENCY_META[code];
        const isActive = selected === code;
        const isDisabled = !available.has(code);
        return (
          <button
            key={code}
            type="button"
            disabled={isDisabled}
            onClick={() => onSelect(code)}
            className={cn(
              'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
              isActive ? 'border-black bg-white' : 'border-gray-200 bg-white hover:border-gray-30',
              isDisabled && 'cursor-not-allowed opacity-40 hover:border-gray-200',
            )}
          >
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F6F6F6] text-sm text-[#222222]">
              {meta.symbol}
            </span>
            <span className="flex flex-1 flex-col">
              <span className="text-sm font-medium text-[#222222]">
                {t(`languageAndCurrency.currencyNames.${code}`)}
              </span>
              <span className="text-xs text-gray-40">{meta.code}</span>
            </span>
            {isActive && <Check className="h-4 w-4 text-[#222222]" />}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
